import type {
  DoorConfig,
  FacadeSide,
  HouseBodyConfig,
  RoomConfig,
  StoryConfig,
  WindowConfig,
} from '@/model/schema';
import type { StylePresetSpec } from './stylePresets';
import type { Rng } from './rng';

/**
 * Window & door placement with alignment rules from design doc §"Openings":
 *  - avoid placing windows too close to corners
 *  - regular spacing, vertical alignment across stacked stories
 *  - larger windows for living rooms, smaller for bathrooms
 *  - front door legible from the primary viewing side
 */

export interface OpeningsInput {
  body: HouseBodyConfig;
  preset: StylePresetSpec;
  /** Per-story rooms, used to pick window sizes per adjacent room type. */
  stories: StoryConfig[];
}

/**
 * Computes windows and a front door, keyed on the facade lengths and the
 * preset's window density. For stacked stories we derive shared X-offsets
 * from the ground floor's openings so windows align vertically.
 */
export function generateOpenings(
  rng: Rng,
  input: OpeningsInput,
): Array<{ windows: WindowConfig[]; doors: DoorConfig[] }> {
  const { body, preset, stories } = input;

  // Collect exterior-wall u-coordinates where a partition meets the outer
  // wall, across all stories. Windows and doors placed on top of these
  // would visibly be sliced by the interior partition. We forbid a 0.45m
  // band around each contact point (≈ partition thickness + visual margin).
  const forbidden = computeForbiddenSpans(body, stories);

  // Per-facade reference anchors from the ground floor, reused upstairs.
  const frontAnchors = sampleFacadeAnchors(rng.fork('front'), body.footprintWidth, preset);
  const backAnchors = sampleFacadeAnchors(rng.fork('back'), body.footprintWidth, preset);
  const leftAnchors = sampleFacadeAnchors(rng.fork('left'), body.footprintDepth, preset);
  const rightAnchors = sampleFacadeAnchors(rng.fork('right'), body.footprintDepth, preset);

  const result: Array<{ windows: WindowConfig[]; doors: DoorConfig[] }> = [];

  for (const story of stories) {
    const isGround = story.level === 0;
    const storyRng = rng.fork(`story-${story.level}`);

    // A single window head height for the entire story keeps the elevation
    // legible — windows sitting on the same wall at different heights look
    // off, regardless of which room is behind them. Width still varies per
    // facade based on the adjacent room type.
    const sharedHeight = pickStoryWindowHeight(storyRng.fork('windowH'), story.rooms, isGround);

    const windows: WindowConfig[] = [];

    const pushSide = (
      side: FacadeSide,
      anchors: number[],
      facadeLen: number,
    ) => {
      const sideForbidden = forbidden[side];
      const placed: Array<[number, number]> = [];
      anchors.forEach((rawOffset, i) => {
        const sizeHint = pickSizeForFacade(storyRng, side, story.rooms, isGround);
        const width = sizeHint.width;
        const height = sharedHeight;
        const rangeLo = preset.minCornerOffset;
        const rangeHi = facadeLen - preset.minCornerOffset;
        if (rangeHi - rangeLo < width) return;
        let offset = Math.max(rangeLo, Math.min(rangeHi - width, rawOffset));

        // Nudge if the desired anchor lands on a partition contact band or
        // overlaps an already-placed window on this facade. This is what
        // was causing compact (and any tightly-partitioned plan) to look
        // window-less — the original code silently dropped the window.
        const blocked = (lo: number) =>
          intersectsForbidden(sideForbidden, lo, lo + width) ||
          placed.some(([plo, phi]) => lo + width > plo && lo < phi);
        if (blocked(offset)) {
          offset = findFreeSlot(
            sideForbidden.concat(placed.map(([a, b]) => [a - 0.2, b + 0.2] as Span)),
            offset,
            width,
            rangeLo,
            rangeHi,
          );
          if (blocked(offset)) return;
        }
        placed.push([offset, offset + width]);
        windows.push({
          id: `W-L${story.level}-${side}-${i}`,
          side,
          offset,
          sillHeight: isGround ? 1.0 : 0.9,
          width,
          height,
          size: sizeHint.size,
        });
      });
    };
    pushSide('front', frontAnchors, body.footprintWidth);
    pushSide('back', backAnchors, body.footprintWidth);
    pushSide('left', leftAnchors, body.footprintDepth);
    pushSide('right', rightAnchors, body.footprintDepth);

    const doors: DoorConfig[] = [];
    if (isGround) {
      const doorWidth = 1.0;
      const doorHeight = 2.1;
      const centerOffset = body.footprintWidth / 2 - doorWidth / 2;
      let offset = Math.max(
        preset.minCornerOffset,
        Math.min(body.footprintWidth - preset.minCornerOffset - doorWidth, centerOffset),
      );
      // If the centered slot would collide with a partition on the front
      // wall, nudge left/right to the nearest clear band.
      if (intersectsForbidden(forbidden.front, offset, offset + doorWidth)) {
        offset = findFreeSlot(
          forbidden.front,
          offset,
          doorWidth,
          preset.minCornerOffset,
          body.footprintWidth - preset.minCornerOffset,
        );
      }
      doors.push({
        id: `D-L0-front`,
        side: 'front',
        offset,
        width: doorWidth,
        height: doorHeight,
        role: 'front',
      });

      // Filter front windows that collide with the door.
      const doorLo = offset - 0.2;
      const doorHi = offset + doorWidth + 0.2;
      for (let i = windows.length - 1; i >= 0; i--) {
        const w = windows[i]!;
        if (w.side !== 'front') continue;
        const wLo = w.offset;
        const wHi = w.offset + w.width;
        if (wHi > doorLo && wLo < doorHi) windows.splice(i, 1);
      }
    }

    result.push({ windows, doors });
  }

  return result;
}

/**
 * Sample evenly spaced offsets along a facade with a small seed-driven
 * jitter. The first and last offsets respect the preset's corner offset.
 */
function sampleFacadeAnchors(rng: Rng, facadeLen: number, preset: StylePresetSpec): number[] {
  const usable = Math.max(0, facadeLen - 2 * preset.minCornerOffset);
  if (usable <= 0) return [];
  const targetCount = Math.max(1, Math.round(facadeLen * preset.windowDensity));
  const stride = usable / targetCount;
  const anchors: number[] = [];
  for (let i = 0; i < targetCount; i++) {
    const center = preset.minCornerOffset + stride * (i + 0.5);
    const jitter = rng.range(-stride * 0.1, stride * 0.1);
    // We store left edge, centered on "center" with assumed window width ~1.1m.
    anchors.push(Math.max(preset.minCornerOffset, center - 0.55 + jitter));
  }
  return anchors;
}

type Span = [number, number];

/**
 * Compute the set of "no-opening" u-coordinate bands per facade. A partition
 * that meets an exterior wall at u=u0 forbids [u0-halfBand, u0+halfBand] on
 * that facade. We union bands across all stories so openings can align
 * vertically and still clear every partition contact.
 */
export function computeFacadeForbiddenSpans(
  body: HouseBodyConfig,
  stories: StoryConfig[],
): Record<FacadeSide, [number, number][]> {
  return computeForbiddenSpans(body, stories);
}

function computeForbiddenSpans(
  body: HouseBodyConfig,
  stories: StoryConfig[],
): Record<FacadeSide, Span[]> {
  const halfBand = 0.45;
  const out: Record<FacadeSide, Span[]> = { front: [], back: [], left: [], right: [] };
  const addBand = (side: FacadeSide, center: number) => {
    out[side].push([center - halfBand, center + halfBand]);
  };
  for (const story of stories) {
    for (const room of story.rooms) {
      const rxEdge = room.x + room.width;
      const rzEdge = room.z + room.depth;
      // +X partition (vertical wall along Z) at x=rxEdge. Touches the front
      // facade if the room starts at z=0, and the back facade if it reaches
      // the rear footprint edge.
      if (rxEdge < body.footprintWidth - 1e-3) {
        if (room.z < 1e-3) addBand('front', rxEdge);
        if (rzEdge > body.footprintDepth - 1e-3) {
          // u on back = footprintWidth - x (see buildWalls.ts for axis map)
          addBand('back', body.footprintWidth - rxEdge);
        }
      }
      // +Z partition (horizontal wall along X) at z=rzEdge. Touches the left
      // facade if the room starts at x=0, the right facade if it reaches the
      // far footprint edge.
      if (rzEdge < body.footprintDepth - 1e-3) {
        if (room.x < 1e-3) {
          addBand('left', body.footprintDepth - rzEdge);
        }
        if (room.x + room.width > body.footprintWidth - 1e-3) {
          addBand('right', rzEdge);
        }
      }
    }
  }
  return out;
}

function intersectsForbidden(spans: Span[], lo: number, hi: number): boolean {
  for (const [a, b] of spans) {
    if (hi > a && lo < b) return true;
  }
  return false;
}

/** Slide `desired` left/right in 0.1m steps until a clear window of
 *  `width` length fits inside [rangeLo, rangeHi - width]. Falls back to the
 *  desired offset if no clean slot exists. */
function findFreeSlot(
  spans: Span[],
  desired: number,
  width: number,
  rangeLo: number,
  rangeHi: number,
): number {
  const maxOffset = rangeHi - width;
  for (let d = 0; d <= maxOffset - rangeLo; d += 0.1) {
    for (const sign of [-1, 1]) {
      const candidate = desired + sign * d;
      if (candidate < rangeLo || candidate > maxOffset) continue;
      if (!intersectsForbidden(spans, candidate, candidate + width)) {
        return candidate;
      }
    }
  }
  return Math.max(rangeLo, Math.min(maxOffset, desired));
}

/**
 * One window head-height per story. We pick from the same heuristic that
 * `pickSizeForFacade` uses, but commit to a single value so every window
 * on this story shares it.
 */
function pickStoryWindowHeight(
  rng: Rng,
  rooms: RoomConfig[],
  isGround: boolean,
): number {
  const types = new Set(rooms.map((r) => r.type));
  if (isGround && (types.has('living') || types.has('kitchen'))) {
    return rng.chance(0.3) ? 1.5 : 1.3;
  }
  if (types.has('bathroom') && !types.has('bedroom')) {
    return 0.9;
  }
  return 1.2;
}

function pickSizeForFacade(
  rng: Rng,
  _side: FacadeSide,
  rooms: RoomConfig[],
  isGround: boolean,
): { width: number; height: number; size: 'small' | 'medium' | 'large' } {
  // Heuristic: if the story has a living/kitchen, pick medium/large; if
  // bathroom-dominant, pick small. We don't do precise room-adjacency math
  // here — that would require geometric intersection between facade segments
  // and room rectangles, which is overkill for v1.
  const types = new Set(rooms.map((r) => r.type));
  if (isGround && (types.has('living') || types.has('kitchen'))) {
    const large = rng.chance(0.3);
    return large
      ? { width: 1.8, height: 1.5, size: 'large' }
      : { width: 1.2, height: 1.3, size: 'medium' };
  }
  if (types.has('bathroom') && !types.has('bedroom')) {
    return { width: 0.7, height: 0.9, size: 'small' };
  }
  return { width: 1.1, height: 1.2, size: 'medium' };
}
